import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import {
  appApi,
  useGetEquipmentListQuery,
  useGetEquipmentProjectsQuery,
} from "../../../../store/appApi";
import { relocateEquipmentApi } from "../../../../services/equipementService";

const FORM_EMPTY = {
  equipmentId: "",
  toProjectId: "",
  transferDate: new Date(),
  approvedBy: "",
  transferNotes: "",
};

const Relocate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: equipData, isLoading: equipLoading } =
    useGetEquipmentListQuery();
  const { data: projectData, isLoading: projectsLoading } =
    useGetEquipmentProjectsQuery();

  const items = equipData?.items || [];
  const projects = projectData?.projects || [];

  const [form, setForm] = useState(FORM_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  // Derive the selected equipment's current project name for display
  const selectedEquip = items.find((eq) => eq._id === form.equipmentId);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.equipmentId) {
      Swal.fire("Validation", "Please select an equipment.", "warning");
      return;
    }
    if (!form.toProjectId) {
      Swal.fire(
        "Validation",
        "Please select a destination project.",
        "warning",
      );
      return;
    }
    if (selectedEquip && selectedEquip.projectId === form.toProjectId) {
      Swal.fire(
        "Validation",
        "Destination is the same as current project.",
        "warning",
      );
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm Relocation",
      html: `Move <strong>${selectedEquip?.eqpName || "equipment"}</strong> to the selected project?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Relocate",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      await relocateEquipmentApi(form.equipmentId, {
        toProjectId: form.toProjectId,
        transferDate: form.transferDate,
        approvedBy: form.approvedBy,
        transferNotes: form.transferNotes,
      });
      dispatch(appApi.util.invalidateTags(["Equipment"]));
      Swal.fire({
        icon: "success",
        title: "Equipment Relocated",
        text: "The relocation record has been saved.",
        timer: 2000,
        showConfirmButton: false,
      });
      setForm(FORM_EMPTY);
      navigate("/progress/equipement/list");
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Failed to relocate.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle
        activeMenu="Relocate Equipment"
        motherMenu="Daily Progress / Equipment"
      />

      <div className="mb-3">
        <Link
          to="/progress/equipement/list"
          className="btn btn-sm btn-outline-secondary"
        >
          &larr; Back to List
        </Link>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="la la-map-marker me-2 text-warning" />
            Transfer / Relocate Asset
          </h5>
        </div>
        <div className="card-body">
          {(equipLoading || projectsLoading) && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}

          {!equipLoading && !projectsLoading && (
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Equipment selector — shows asset name + current location */}
                <div className="col-md-4 form-group">
                  <label className="font-weight-bold">
                    Equipment <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="equipmentId"
                    value={form.equipmentId}
                    onChange={handleChange}
                  >
                    <option value="">-- Select Equipment --</option>
                    {items.map((eq) => (
                      <option key={eq._id} value={eq._id}>
                        {eq.eqpName}
                        {eq.eqpNumber ? ` (${eq.eqpNumber})` : ""}
                        {" — "}
                        {eq.projectName || "No project"}
                      </option>
                    ))}
                  </select>
                  {selectedEquip && (
                    <small className="text-muted mt-1 d-block">
                      Current Location:{" "}
                      <strong>
                        {selectedEquip.projectName || "Not assigned"}
                      </strong>
                    </small>
                  )}
                </div>

                {/* Destination project selector */}
                <div className="col-md-4 form-group">
                  <label className="font-weight-bold">
                    Destination Project <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="toProjectId"
                    value={form.toProjectId}
                    onChange={handleChange}
                  >
                    <option value="">-- Select Project --</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Transfer Date */}
                <div className="col-md-4 form-group d-flex flex-column">
                  <label className="font-weight-bold">Transfer Date</label>
                  <DatePicker
                    selected={form.transferDate}
                    onChange={(d) =>
                      setForm((prev) => ({ ...prev, transferDate: d }))
                    }
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select date"
                  />
                </div>

                {/* Approved By */}
                <div className="col-md-4 form-group">
                  <label className="font-weight-bold">Approved By</label>
                  <input
                    type="text"
                    className="form-control"
                    name="approvedBy"
                    value={form.approvedBy}
                    onChange={handleChange}
                    placeholder="Approver name"
                  />
                </div>

                {/* Transfer Notes */}
                <div className="col-md-4 form-group">
                  <label className="font-weight-bold">Transfer Notes</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="transferNotes"
                    value={form.transferNotes}
                    onChange={handleChange}
                    placeholder="Reason for transfer, condition at time of move…"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-warning"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                    />
                    Relocating…
                  </>
                ) : (
                  "Transfer Asset"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default Relocate;
